import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { unifiedTasksApi, UnifiedTask } from '../api/unifiedTasks';
import { laneApi } from '../api/laneApi';
import { fetchClaims, getClaim } from '../api/claims';
import PDFViewerDrawer from '../components/PDFViewerDrawer';
import { RECEIVED_FROM_1_JULY_2026 } from '../constants/receivedFrom';
import './Tasks.css';

/** Post lane "Claim Ref" -- prefer the real, undropped post_claim_ref
 * column (survives the Tasks Rebuild POST-SOAK cleanup migration nulling
 * unified_tasks_view's emailContent projection); fall back to
 * emailContent.claimRef for rows fetched before that column existed on the
 * list response, or in any era where emailContent is still populated. */
const getPostClaimRef = (task: UnifiedTask): string | undefined =>
  task.postClaimRef || task.emailContent?.claimRef || undefined;

interface PaginatedTasks {
  tasks: UnifiedTask[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

const ITEMS_PER_PAGE = 50;

const formatDateTime = (value?: string) => {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

const formatConfidence = (confidence?: number) => {
  if (confidence === undefined || confidence === null) return '—';
  const percentage = Math.round(confidence * 1000) / 10;
  return `${percentage.toFixed(1)}%`;
};

export const PostTasks: React.FC = () => {
  const [pendingTasks, setPendingTasks] = useState<PaginatedTasks>({
    tasks: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
  });
  const [completedTasks, setCompletedTasks] = useState<PaginatedTasks>({
    tasks: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState<'pending' | 'completed'>('pending');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTask, setSelectedTask] = useState<UnifiedTask | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [destination, setDestination] = useState<{ claimId: string; reference: string } | null>(null);
  const destinationId = selectedTask?.assignedClaimId;

  // Resolve only the open detail's destination; never add a claim read per
  // list row or mistake the scanned filename reference for the assigned claim.
  useEffect(() => {
    let cancelled = false;
    setDestination(null);
    if (viewDialogOpen && destinationId) {
      getClaim(destinationId).then(claim => {
        if (!cancelled) setDestination({
          claimId: destinationId,
          reference: claim.proclaimReference || (claim as { reference?: string }).reference || '',
        });
      }).catch(() => {
        if (!cancelled) setDestination({ claimId: destinationId, reference: '' });
      });
    }
    return () => { cancelled = true; };
  }, [viewDialogOpen, destinationId]);
  const [processing, setProcessing] = useState(false);
  // Review-fix (Tasks Rebuild Task 15 cleanup migration): the document
  // link is fetched on demand via GET /ui/tasks/<id>/content -- the same
  // content side-table route the detail modal below already reads from --
  // rather than relying solely on selectedTask.emailContent?.documentUrl.
  // Works identically before and after the POST-SOAK cleanup migration
  // drops the in-row emailContent column (that route never reads it), and
  // the route refreshes the SAS token on every call so the link doesn't
  // go stale between task creation and someone opening this dialog.
  const [documentContentUrl, setDocumentContentUrl] = useState<string | null>(null);

  // Since-1-July preset (ticket #388), mirroring the Tasks page lender lane:
  // defaults to on so Post opens scoped to the post-1-July backlog; the chip
  // lets the user dismiss it for this visit. Component state means it
  // re-arms automatically the next time this page mounts.
  const [receivedFromActive, setReceivedFromActive] = useState(true);

  // Reassignment state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignSearchTerm, setReassignSearchTerm] = useState('');
  const [reassignSuggestions, setReassignSuggestions] = useState<any[]>([]);
  const [reassignSelectedClaim, setReassignSelectedClaim] = useState<any | null>(null);
  const [reassignReason, setReassignReason] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<{ url: string; fileName: string } | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 500);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const fetchTasksForStatus = useCallback(async (status: 'pending' | 'completed', page: number): Promise<PaginatedTasks> => {
    setIsSearching(true);
    const response = await unifiedTasksApi.listTasks({
      status,
      taskType: 'post',
      page,
      pageSize: ITEMS_PER_PAGE,
      searchTerm: debouncedSearchTerm || undefined,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      // Applied to both Pending and Completed so the tab counts (server
      // totals) reflect the filter the same way the Tasks page lender lane
      // does — see receivedFromActive chip below.
      receivedFrom: receivedFromActive ? RECEIVED_FROM_1_JULY_2026 : undefined,
    });

    return {
      tasks: response.tasks,
      // PostTasks never requests skipCount, so totals are always present;
      // coalesce for the shared (nullable) TaskListResponse type.
      totalCount: response.totalCount ?? 0,
      currentPage: response.currentPage,
      totalPages: response.totalPages ?? 0,
    };
  }, [debouncedSearchTerm, receivedFromActive]);

  const loadTasks = useCallback(async (pendingPage = 1, completedPage = 1) => {
    try {
      setLoading(true);
      const [pending, completed] = await Promise.all([
        fetchTasksForStatus('pending', pendingPage),
        fetchTasksForStatus('completed', completedPage),
      ]);
      setPendingTasks(pending);
      setCompletedTasks(completed);
      setMessage(null);
    } catch (error) {
      console.error('Failed to load post tasks', error);
      setMessage({ type: 'error', text: 'Failed to load post tasks' });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [fetchTasksForStatus]);

  const handlePageChange = useCallback(
    (status: 'pending' | 'completed', page: number) => {
      if (page < 1) return;
      if (status === 'pending') {
        const max = pendingTasks.totalPages || 1;
        const nextPage = Math.min(page, max);
        loadTasks(nextPage, completedTasks.currentPage);
      } else {
        const max = completedTasks.totalPages || 1;
        const nextPage = Math.min(page, max);
        loadTasks(pendingTasks.currentPage, nextPage);
      }
    },
    [completedTasks.currentPage, completedTasks.totalPages, loadTasks, pendingTasks.currentPage, pendingTasks.totalPages]
  );

  const renderPagination = useCallback(
    (data: PaginatedTasks, status: 'pending' | 'completed') => {
      if (data.totalPages <= 1) {
        return null;
      }

      const pages: number[] = [];
      const maxPagesToShow = 7;
      let startPage = Math.max(1, data.currentPage - 3);
      let endPage = Math.min(data.totalPages, startPage + maxPagesToShow - 1);

      if (endPage - startPage < maxPagesToShow - 1) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      for (let i = startPage; i <= endPage; i += 1) {
        pages.push(i);
      }

      const goToPage = (target: number) => handlePageChange(status, target);

      return (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => goToPage(1)}
            disabled={loading || data.currentPage === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => goToPage(data.currentPage - 1)}
            disabled={loading || data.currentPage === 1}
          >
            Previous
          </button>
          {pages.map((page) => (
            <button
              key={page}
              className={`pagination-btn ${page === data.currentPage ? 'active' : ''}`}
              onClick={() => goToPage(page)}
              disabled={loading}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => goToPage(data.currentPage + 1)}
            disabled={loading || data.currentPage === data.totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => goToPage(data.totalPages)}
            disabled={loading || data.currentPage === data.totalPages}
          >
            Last
          </button>
        </div>
      );
    },
    [handlePageChange, loading]
  );

  useEffect(() => {
    loadTasks(1, 1);
  }, [loadTasks]);

  useEffect(() => {
    // when search changes, reload from first page
    if (!loading) {
      loadTasks(1, 1);
    }
  }, [debouncedSearchTerm]);

  const handleComplete = async (task: UnifiedTask) => {
    if (!window.confirm('Mark this post task as completed?')) {
      return;
    }

    setProcessing(true);
    try {
      await unifiedTasksApi.completeTask(task.id, 'Post document processed');
      setMessage({ type: 'success', text: 'Task marked as completed' });
      loadTasks(tabValue === 'pending' ? pendingTasks.currentPage : completedTasks.currentPage,
                completedTasks.currentPage);
      setViewDialogOpen(false);
      setSelectedTask(null);
    } catch (error) {
      console.error('Failed to complete task', error);
      setMessage({ type: 'error', text: 'Failed to complete task' });
    } finally {
      setProcessing(false);
    }
  };

  const openViewDialog = async (task: UnifiedTask) => {
    // Open instantly with the list-payload task, then swap in the full
    // detail row: claudeAnalysis reasoning/snippet and emailContent are
    // trimmed from the list response (see LIST_VIEW_COLUMNS) and only
    // returned by the single-task fetch, same as the main Tasks page.
    setSelectedTask(task);
    setViewDialogOpen(true);
    setDocumentContentUrl(null);
    try {
      const fullTask = await unifiedTasksApi.getTask(task.id);
      setSelectedTask(fullTask);
    } catch (error) {
      console.error('Error fetching task details:', error);
      // Keep the list-payload task if the fetch fails — Category/Confidence
      // (synthesized server-side) will still show; reasoning/snippet won't.
    }
    // Fetch the document link separately via the content side-table route
    // (see documentContentUrl above) -- independent of the full-task fetch
    // above so "Open Document" still gets a fresh link even if that fetch
    // fails, and so it works whether or not the in-row emailContent column
    // still exists.
    try {
      const content = await laneApi.getTaskContent(task.id);
      const url = content.emailContent?.documentUrl;
      if (typeof url === 'string' && url) {
        setDocumentContentUrl(url);
      }
    } catch (error) {
      console.error('Error fetching task document content:', error);
      // Open Document falls back to selectedTask.emailContent?.documentUrl.
    }
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedTask(null);
    setDocumentContentUrl(null);
  };

  const openReassignDialog = () => {
    setReassignDialogOpen(true);
    setReassignSearchTerm('');
    setReassignSuggestions([]);
    setReassignSelectedClaim(null);
    setReassignReason('');
  };

  const closeReassignDialog = () => {
    setReassignDialogOpen(false);
    setReassignSearchTerm('');
    setReassignSuggestions([]);
    setReassignSelectedClaim(null);
    setReassignReason('');
  };

  // Search for claims when reassigning
  useEffect(() => {
    if (reassignSearchTerm.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          const response = await fetchClaims(1, 10, reassignSearchTerm);
          setReassignSuggestions(response.items || []);
        } catch (error) {
          console.error('Failed to search claims:', error);
          setReassignSuggestions([]);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setReassignSuggestions([]);
    }
  }, [reassignSearchTerm]);

  const handleReassign = async () => {
    if (!selectedTask || !reassignSelectedClaim) return;

    setReassigning(true);
    try {
      await unifiedTasksApi.reassignTask(
        selectedTask.id,
        reassignSelectedClaim.id,
        reassignReason || undefined
      );
      setMessage({ type: 'success', text: 'Task reassigned successfully' });
      closeReassignDialog();
      closeViewDialog();
      loadTasks(pendingTasks.currentPage, completedTasks.currentPage);
    } catch (error) {
      console.error('Failed to reassign task:', error);
      setMessage({ type: 'error', text: 'Failed to reassign task' });
    } finally {
      setReassigning(false);
    }
  };

  const renderClassification = (task: UnifiedTask) => {
    const analysis = task.claudeAnalysis;
    if (!analysis) return '—';
    if (analysis.category) {
      return `${analysis.category} (${formatConfidence(analysis.confidence)})`;
    }
    return formatConfidence(analysis.confidence);
  };

  const sortedPendingTasks = useMemo(() => {
    return [...pendingTasks.tasks].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [pendingTasks.tasks]);

  const sortedCompletedTasks = useMemo(() => {
    return [...completedTasks.tasks].sort((a, b) => new Date(b.completedAt || b.createdAt).getTime() - new Date(a.completedAt || a.createdAt).getTime());
  }, [completedTasks.tasks]);

  return (
    <div className="tasks-container">
      {isSearching && (
        <div className="loading-overlay">
          <div className="spinner" />
          <p>Searching tasks...</p>
        </div>
      )}

      <div className="tasks-header">
        <h1>Post Tasks</h1>
        <div className="header-controls">
          {receivedFromActive && (
            <span className="task-filter-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '2px 10px', borderRadius: 12, background: '#e8f0fe', fontSize: 13 }}>
              Showing post received since 1 Jul 2026
              <button onClick={() => setReceivedFromActive(false)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                aria-label="Show all post tasks">✕</button>
            </span>
          )}
          <input
            type="text"
            placeholder="Search by client or claim..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="task-search-input"
          />
          <button className="btn-secondary" onClick={() => loadTasks(pendingTasks.currentPage, completedTasks.currentPage)}>
            Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className={`global-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab ${tabValue === 'pending' ? 'active' : ''}`}
          onClick={() => setTabValue('pending')}
        >
          Pending
          {pendingTasks.totalCount > 0 && <span className="badge">{pendingTasks.totalCount}</span>}
        </button>
        <button
          className={`tab ${tabValue === 'completed' ? 'active' : ''}`}
          onClick={() => setTabValue('completed')}
        >
          Completed
          {completedTasks.totalCount > 0 && <span className="badge secondary">{completedTasks.totalCount}</span>}
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner" />
        </div>
      ) : (
        <>
          {tabValue === 'pending' ? (
            <div className="tasks-table-container">
              <table className="tasks-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Claim Ref</th>
                    <th>Category</th>
                    <th>Confidence</th>
                    <th>Received</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPendingTasks.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">No pending post tasks found</td>
                    </tr>
                  ) : (
                    sortedPendingTasks.map(task => (
                      <tr key={task.id}>
                        <td>{task.clientName || '-'}</td>
                        <td>{getPostClaimRef(task) || '-'}</td>
                        <td>{renderClassification(task)}</td>
                        <td>{formatConfidence(task.claudeAnalysis?.confidence)}</td>
                        <td>{formatDateTime(task.createdAt)}</td>
                        <td className="actions">
                          <button className="action-btn-compact view" onClick={() => openViewDialog(task)}>
                            View
                          </button>
                          <button
                            className="action-btn-compact complete"
                            onClick={() => handleComplete(task)}
                            disabled={processing}
                          >
                            Complete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {renderPagination(pendingTasks, 'pending')}
            </div>
          ) : (
            <div className="tasks-table-container">
              <table className="tasks-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Claim Ref</th>
                    <th>Category</th>
                    <th>Confidence</th>
                    <th>Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCompletedTasks.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">No completed post tasks found</td>
                    </tr>
                  ) : (
                    sortedCompletedTasks.map(task => (
                      <tr key={task.id}>
                        <td>{task.clientName || '-'}</td>
                        <td>{getPostClaimRef(task) || '-'}</td>
                        <td>{renderClassification(task)}</td>
                        <td>{formatConfidence(task.claudeAnalysis?.confidence)}</td>
                        <td>{formatDateTime(task.completedAt)}</td>
                        <td className="actions">
                          <button className="action-btn-compact view" onClick={() => openViewDialog(task)}>
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {renderPagination(completedTasks, 'completed')}
            </div>
          )}
        </>
      )}

      {viewDialogOpen && selectedTask && (
        <div className="modal-backdrop" onClick={closeViewDialog}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Post Document Details</h2>
            <div className="manual-task-content-section" style={{ marginTop: '12px' }}>
              <div className="detail-row"><strong>Client:</strong> {selectedTask.clientName || 'Unknown'}</div>
              <div className="detail-row"><strong>Scanned reference:</strong> {getPostClaimRef(selectedTask) || 'Unknown'}</div>
              <div className="detail-row"><strong>Received:</strong> {formatDateTime(selectedTask.createdAt)}</div>
              {selectedTask.assignedClaimId && (
                <>
                  <div className="detail-row"><strong>Destination claim:</strong>{' '}
                    {destination?.claimId === selectedTask.assignedClaimId ? (
                      destination.reference ? <a href={`/?openClaimId=${encodeURIComponent(selectedTask.assignedClaimId)}`}>{destination.reference}</a> : 'Reference unavailable'
                    ) : 'Loading…'}
                  </div>
                  <div className="detail-row"><strong>Assigned Claim ID:</strong> {selectedTask.assignedClaimId}</div>
                </>
              )}
            </div>

            <div className="ai-analysis-section" style={{ marginTop: '16px' }}>
              <h3>Classification</h3>
              <div className="ai-analysis-content">
                <div className="detail-row"><strong>Category:</strong> {selectedTask.claudeAnalysis?.category || 'Unknown'}</div>
                <div className="detail-row"><strong>Confidence:</strong> {formatConfidence(selectedTask.claudeAnalysis?.confidence)}</div>
                {selectedTask.claudeAnalysis?.reasoning && (
                  <div className="detail-row">
                    <strong>Reasoning:</strong>
                    <p style={{ marginTop: '8px' }}>{selectedTask.claudeAnalysis.reasoning}</p>
                  </div>
                )}
                {selectedTask.claudeAnalysis?.snippet && (
                  <div className="detail-row">
                    <strong>Snippet:</strong>
                    <pre className="ai-notes-content">{selectedTask.claudeAnalysis.snippet}</pre>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button
                className="btn-secondary"
                onClick={() => {
                  const url = documentContentUrl || selectedTask.emailContent?.documentUrl;
                  if (url) {
                    // Check if it's a PDF (handle URLs with query params)
                    const isPdf = url.toLowerCase().includes('.pdf');
                    if (isPdf) {
                      setSelectedPdf({
                        url,
                        fileName: selectedTask.subject || 'Document'
                      });
                      setPdfViewerOpen(true);
                    } else {
                      window.open(url, '_blank', 'noopener');
                    }
                  }
                }}
              >
                Open Document
              </button>
              {selectedTask.assignedClaimId && (
                <button
                  className="btn-secondary"
                  onClick={openReassignDialog}
                  disabled={processing}
                >
                  Reassign
                </button>
              )}
              {selectedTask.status !== 'completed' && (
                <button
                  className="btn-primary"
                  onClick={() => handleComplete(selectedTask)}
                  disabled={processing}
                >
                  Mark Completed
                </button>
              )}
              <button className="btn-secondary" onClick={closeViewDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassignment Dialog */}
      {reassignDialogOpen && selectedTask && (
        <div className="modal-backdrop" onClick={closeReassignDialog}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>Reassign Post Task</h2>

            <div className="manual-task-content-section" style={{ marginTop: '12px' }}>
              <div className="detail-row">
                <strong>Current Claim:</strong> {selectedTask.assignedClaimId || 'Not assigned'}
              </div>
              <div className="detail-row">
                <strong>Client:</strong> {selectedTask.clientName || 'Unknown'}
              </div>
              <div className="detail-row">
                <strong>Category:</strong> {selectedTask.claudeAnalysis?.category || 'Unknown'}
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <label htmlFor="claim-search" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Search for New Claim:
              </label>
              <input
                id="claim-search"
                type="text"
                value={reassignSearchTerm}
                onChange={(e) => {
                  setReassignSearchTerm(e.target.value);
                  setReassignSelectedClaim(null);
                }}
                placeholder="Enter claim ID, name, or email..."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                autoFocus
              />
            </div>

            {reassignSuggestions.length > 0 && (
              <div style={{
                marginTop: '12px',
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: '4px'
              }}>
                {reassignSuggestions.map((claim) => (
                  <div
                    key={claim.id}
                    onClick={() => setReassignSelectedClaim(claim)}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      backgroundColor: reassignSelectedClaim?.id === claim.id ? '#e6f2ff' : 'white',
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {claim.id}
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748b' }}>
                      {claim.firstName} {claim.lastName} • {claim.email}
                    </div>
                    {claim.craFinanceLender && (
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                        Lender: {claim.craFinanceLender}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {reassignSearchTerm.length >= 2 && reassignSuggestions.length === 0 && (
              <div style={{ marginTop: '12px', padding: '12px', color: '#64748b', textAlign: 'center' }}>
                No claims found matching "{reassignSearchTerm}"
              </div>
            )}

            <div style={{ marginTop: '16px' }}>
              <label htmlFor="reassign-reason" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Reason (optional):
              </label>
              <textarea
                id="reassign-reason"
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="Enter reason for reassignment..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button
                className="btn-secondary"
                onClick={closeReassignDialog}
                disabled={reassigning}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleReassign}
                disabled={!reassignSelectedClaim || reassigning}
              >
                {reassigning ? 'Reassigning...' : 'Reassign Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Drawer */}
      {selectedPdf && (
        <PDFViewerDrawer
          open={pdfViewerOpen}
          onClose={() => {
            setPdfViewerOpen(false);
            setSelectedPdf(null);
          }}
          pdfUrl={selectedPdf.url}
          fileName={selectedPdf.fileName}
        />
      )}
    </div>
  );
};

export default PostTasks;
